// Developer: Ankit Gupta
/**
 * Which project and data source Insight opens on when the URL does not say.
 *
 * The server suggests one (the project whose dashboard the user last opened,
 * else the busiest). That suggestion knows nothing about what the person
 * picked in this browser, so a user who moved to a working project was put
 * back every morning on the one whose source had been failing for days. The
 * choice made here, in order:
 *
 *   1. the URL, when it names a project the user can reach;
 *   2. the last project and data source this user picked in this browser;
 *   3. the server's suggestion;
 *   4. the first project.
 *
 * The data source follows the same order within the chosen project, and falls
 * back to the project's source environment, then to its first.
 */

import { isNotSqlEnv } from '@/utils/environments';

export interface ScopeEnvironment {
  id: number;
  type: string;
}

export interface ScopeProject<E extends ScopeEnvironment = ScopeEnvironment> {
  project_id: number;
  environments: E[];
}

export interface RememberedScope {
  project: number;
  env: number | null;
}

export const SCOPE_STORAGE_KEY = 'insight.scope';

export function readRememberedScope(): RememberedScope | null {
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (raw) {
      const value = JSON.parse(raw) as Partial<RememberedScope>;
      if (typeof value.project === 'number') {
        return { project: value.project, env: typeof value.env === 'number' ? value.env : null };
      }
    }
    const dashProj = window.localStorage.getItem('dp.dashboard.project');
    if (dashProj && !isNaN(Number(dashProj))) {
      return { project: Number(dashProj), env: null };
    }
    return null;
  } catch {
    return null;
  }
}

export function rememberScope(project: number, env: number | null): void {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify({ project, env }));
  } catch {
    // A browser that refuses storage still works; it just does not remember.
  }
}

export interface ScopeInput<P extends ScopeProject> {
  projects: P[];
  urlProject: number | null;
  urlEnv: number | null;
  remembered: RememberedScope | null;
  /**
   * The server's suggestion: undefined while it is still loading, null when
   * none is coming (the URL named a project, so it was never asked for).
   */
  suggested: { project_id: number | null; environment_id: number | null } | null | undefined;
}

export interface ScopeChoice<P extends ScopeProject> {
  project: P;
  env: P['environments'][number] | undefined;
}

/**
 * The scope to settle on, or null to wait: with nothing in the URL and nothing
 * remembered, it waits for the server's suggestion rather than settling on the
 * first project and then moving the user.
 */
export function chooseScope<P extends ScopeProject>(input: ScopeInput<P>): ScopeChoice<P> | null {
  const { projects, urlProject, urlEnv, remembered, suggested } = input;
  if (projects.length === 0) return null;
  const byId = (id: number | null | undefined) =>
    id == null ? undefined : projects.find((project) => project.project_id === id);

  const fromUrl = byId(urlProject);
  const fromMemory = fromUrl ? undefined : byId(remembered?.project);
  if (!fromUrl && !fromMemory && suggested === undefined) return null;
  const fromServer = fromUrl || fromMemory ? undefined : byId(suggested?.project_id);
  const project = fromUrl ?? fromMemory ?? fromServer ?? projects[0];

  const envIn = (id: number | null | undefined) =>
    id == null ? undefined : project.environments.find((environment) => environment.id === id);
  const env =
    (fromUrl && envIn(urlEnv)) ||
    (fromMemory && envIn(remembered?.env)) ||
    (fromServer && envIn(suggested?.environment_id)) ||
    project.environments.find((environment) => environment.type === 'SOURCE' && !isNotSqlEnv(environment)) ||
    project.environments.find((environment) => !isNotSqlEnv(environment));
  return { project, env };
}
